<template>
  <div class="time-combobox" role="combobox" :aria-expanded="open ? 'true' : 'false'" aria-haspopup="listbox">
    <input
      ref="input"
      type="text"
      class="input time-combobox__input"
      :class="inputClass"
      :value="query"
      :placeholder="placeholder"
      :disabled="isDisabled"
      autocomplete="off"
      role="textbox"
      aria-autocomplete="list"
      :aria-controls="listId"
      :aria-activedescendant="activeIndex >= 0 ? optionId(activeIndex) : null"
      @focus="openList"
      @click="openList"
      @input="onInput"
      @keydown="onKeydown"
      @blur="onBlur"
    />
    <ul
      v-show="open"
      :id="listId"
      ref="list"
      class="time-combobox__list"
      role="listbox"
    >
      <li
        v-for="(option, index) in filteredOptions"
        :key="option"
        :id="optionId(index)"
        role="option"
        class="time-combobox__option"
        :class="{ 'is-active': index === activeIndex }"
        :aria-selected="index === activeIndex ? 'true' : 'false'"
        @mousedown.prevent="selectOption(option)"
      >
        {{ option }}
      </li>
    </ul>
  </div>
</template>

<script>
import { filterTimeOptions } from '../time_options';

export default {
  props: {
    value: { type: String, default: '' },
    options: { type: Array, default: () => [] },
    disabled: { type: [Boolean, String], default: false },
    placeholder: { type: String, default: '' },
    inputClass: { type: String, default: '' }
  },

  data() {
    return {
      query: this.value,
      open: false,
      typing: false,
      activeIndex: -1
    };
  },

  computed: {
    isDisabled() {
      return String(this.disabled) === 'true' || this.disabled === true;
    },
    // Blank query (menu just opened) => full list, so the current value never
    // suppresses the options; typing narrows it.
    filteredOptions() {
      return filterTimeOptions(this.options, this.typing ? this.query : '');
    },
    listId() {
      return 'time-combobox-list-' + this._uid;
    }
  },

  watch: {
    // Keep in sync when the value is changed externally (e.g. Duration editing
    // recomputes the End time).
    value(newValue) {
      this.query = newValue;
    }
  },

  methods: {
    optionId(index) {
      return 'time-combobox-option-' + this._uid + '-' + index;
    },

    openList() {
      if (this.isDisabled) return;
      this.open = true;
      this.typing = false;
      this.activeIndex = this.filteredOptions.indexOf(this.value);
      this.$nextTick(this.scrollActiveIntoView);
    },

    close() {
      this.open = false;
      this.typing = false;
      this.activeIndex = -1;
    },

    onInput(event) {
      this.query = event.target.value;
      this.typing = true;
      this.open = true;
      this.activeIndex = -1;
    },

    onKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (!this.open) { this.openList(); return; }
          this.moveActive(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (!this.open) { this.openList(); return; }
          this.moveActive(-1);
          break;
        case 'Enter':
          if (this.open && this.activeIndex >= 0) {
            event.preventDefault();
            this.selectOption(this.filteredOptions[this.activeIndex]);
          } else {
            event.preventDefault();
            this.commit();
          }
          break;
        case 'Escape':
          if (this.open) {
            event.preventDefault();
            event.stopPropagation();
            this.query = this.value; // revert, do not change
            this.close();
          }
          break;
        case 'Tab':
          this.commit(); // let focus move naturally
          break;
        default:
          break;
      }
    },

    moveActive(delta) {
      const count = this.filteredOptions.length;
      if (count === 0) { this.activeIndex = -1; return; }
      const next = this.activeIndex + delta;
      this.activeIndex = (next + count) % count;
      this.$nextTick(this.scrollActiveIntoView);
    },

    scrollActiveIntoView() {
      const list = this.$refs.list;
      if (!list || this.activeIndex < 0) return;
      const el = list.children[this.activeIndex];
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    },

    selectOption(option) {
      this.query = option;
      if (option !== this.value) this.$emit('change', option);
      this.close();
    },

    onBlur() {
      // Fires on outside click / tabbing away; commit the current text.
      this.commit();
    },

    commit() {
      // Only emit when the text actually changed (mirrors v-model.lazy), so an
      // unchanged blur does not trigger spurious downstream updates.
      if (this.query !== this.value) this.$emit('change', this.query);
      this.close();
      // Re-sync to the normalized value (or revert if the parent rejected it).
      this.$nextTick(() => { this.query = this.value; });
    }
  }
};
</script>

<style lang="scss" scoped>
.time-combobox {
  position: relative;
  // Comfortably fits 12-hour values like "11:30 PM" while giving the adjacent
  // date control more room for a full year. Rebalanced -16px toward the date
  // control so the Schedule row keeps its overall width.
  min-width: 94px;

  &__input {
    width: 100%;
  }

  &__list {
    position: absolute;
    z-index: 20;
    left: 0;
    right: 0;
    margin: 2px 0 0;
    padding: 4px 0;
    max-height: 220px;
    overflow-y: auto;
    list-style: none;
    background: #fff;
    border: 1px solid #d6dbe0;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &__option {
    padding: 6px 12px;
    cursor: pointer;
    white-space: nowrap;

    &:hover,
    &.is-active {
      background-color: #eef4fb;
    }
  }
}
</style>
